
import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { fetchBlockByHeight } from '../services/apiService';
import useTitle from '../hooks/useTitle';
import {
  FaCube,
  FaCalendarAlt,
  FaLock,
  FaUsers,
  FaCogs,
  FaKey,
  FaChartLine,
  FaHashtag,
  FaArrowUp,
  FaArrowDown,
  FaExpandAlt,
  FaCompressAlt,
} from 'react-icons/fa';

const BlockHeightPage = () => {
  useTitle("MENELIK - Block Height");
  const { blockHeight } = useParams();
  const [blockData, setBlockData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expandedTx, setExpandedTx] = useState({});

  useEffect(() => {
    if (blockHeight) {
      fetchBlockByHeight(blockHeight).then((data) => {
        setBlockData(data);
        setLoading(false);
      });
    }
  }, [blockHeight]);

  const toggleTxDetails = (txid) => {
    setExpandedTx((prev) => ({
      ...prev,
      [txid]: !prev[txid],
    }));
  };

  if (loading) return <p>Loading block data...</p>;
  if (!blockData) return <p>Block data not found.</p>;

  return (
    <div className="container mx-auto p-4">
      <h1 className="text-2xl font-bold mb-4 flex items-center">
        <FaCube className="mr-2" /> Block Details - Height {blockHeight}
      </h1>
      <div className="bg-white shadow-md rounded p-4 mb-6">
        <p><FaHashtag className="inline mr-2" /><strong>Block Hash:</strong> {blockData.block_hash}</p>
        <p><FaLock className="inline mr-2" /><strong>Height:</strong> {blockData.height}</p>
        <p>
          <FaCalendarAlt className="inline mr-2" />
          <strong>Timestamp:</strong> {new Date(blockData.time).toLocaleString()}
        </p>
        <p><FaChartLine className="inline mr-2" /><strong>Difficulty:</strong> {blockData.difficulty}</p>
        <p><FaUsers className="inline mr-2" /><strong>Confirmations:</strong> {blockData.confirmations}</p>
        <p><FaCogs className="inline mr-2" /><strong>Version:</strong> {blockData.version} ({blockData.versionHex})</p>
        <p><FaKey className="inline mr-2" /><strong>Merkle Root:</strong> {blockData.merkleroot}</p>
        <p><FaArrowUp className="inline mr-2" /><strong>Previous Block:</strong> {blockData.previousblockhash || 'N/A'}</p>
        <p><FaArrowDown className="inline mr-2" /><strong>Next Block:</strong> {blockData.nextblockhash || 'N/A'}</p>
        <p><FaChartLine className="inline mr-2" /><strong>Chainwork:</strong> {blockData.chainwork}</p>
        <p><FaCogs className="inline mr-2" /><strong>Nonce:</strong> {blockData.nonce}</p>
        <p><FaUsers className="inline mr-2" /><strong>Number of Transactions:</strong> {blockData.nTx}</p>
      </div>

      <h2 className="text-xl font-semibold mb-4">Transactions</h2>
      <table className="table-auto w-full text-left bg-gray-100 shadow-md rounded mb-6">
        <thead className="bg-gray-200">
          <tr>
            <th className="p-3 border-b">Tx Hash</th>
            <th className="p-3 border-b">Recipients</th>
            <th className="p-3 border-b">Amount</th>
          </tr>
        </thead>
        <tbody>
          {blockData.transactions.map((tx) => {
            const totalValue = tx.recipients.reduce((sum, r) => sum + parseFloat(r.value || 0), 0).toFixed(8);
            const numRecipients = tx.recipients.length;

            return (
              <React.Fragment key={tx.txid}>
                <tr className="hover:bg-gray-50">
                  <td className="p-3 border-b truncate">{tx.txid}</td>
                  <td className="p-3 border-b">{numRecipients}</td>
                  <td className="p-3 border-b">{totalValue} MENEL</td>
                  <td className="p-3 border-b">
                    <button
                      className="text-blue-500 hover:underline flex items-center"
                      onClick={() => toggleTxDetails(tx.txid)}
                    >
                      {expandedTx[tx.txid] ? (
                        <>
                          <FaCompressAlt className="mr-1" /> Collapse
                        </>
                      ) : (
                        <>
                          <FaExpandAlt className="mr-1" /> Expand
                        </>
                      )}
                    </button>
                  </td>
                </tr>

                {expandedTx[tx.txid] && (
                  <tr>
                    <td colSpan="3" className="p-3 bg-white">
                      <div className="bg-gray-50 shadow-inner rounded p-3">
                        <h3 className="text-lg font-semibold mb-2">Recipient Details:</h3>
                        <table className="table-auto w-full text-left">
                          <thead>
                            <tr>
                              <th className="p-2 border-b">Address</th>
                              <th className="p-2 border-b">Value (MENEL)</th>
                            </tr>
                          </thead>
                          <tbody>
                            {tx.recipients.map((recipient, index) => (
                              <tr key={index} className="hover:bg-gray-100">
                                <td className="p-2 border-b">{recipient.address}</td>
                                <td className="p-2 border-b">{recipient.value}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </td>
                  </tr>
                )}
              </React.Fragment>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

export default BlockHeightPage;
